import React, { useRef } from "react";
import { Table, Container, Card } from "react-bootstrap";
import { FaPlus, FaHome, FaFilePdf } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import dataHoraFormatada from "../../hooks/Date/dataHoraFormatada";
import dataFormatada from "../../hooks/Date/dataFormatada";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Register pdfMake fonts
pdfMake.vfs = pdfFonts.vfs;

const RelatorioHistoricoManutencao = ({ filtro, dataHora, dados, setShowForm, setShowRelatorio }) => {
  const navigate = useNavigate();
  const chartRef = useRef(null);

  const handleGoHome = () => {
    navigate("/home");
  };

  const handleForm = () => {
    setShowForm(true);
    setShowRelatorio(false);
  };

  const generatePDF = async () => {
    const chart = chartRef.current;
    const chartBase64 = chart.toBase64Image();
  
    const tableBody = [
      [
        { text: 'Unidade', style: 'tableHeader' },
        { text: 'Ordens de Serviço', style: 'tableHeader' },
        { text: 'Tempo de Manutenção', style: 'tableHeader' },
        { text: 'Peças Utilizadas', style: 'tableHeader' }
      ]
    ];
  
    dados.data.data.forEach(unidade => {
      unidade.ordens_servico.forEach(ordem => {
        tableBody.push([
          { text: unidade.nome || 'Unidade não informada', style: 'tableData' },
          { text: `${ordem.id || 'N/A'} - ${ordem.solicitacao || 'N/A'}`, style: 'tableData' },
          { text: ordem.tempo_total_manutencao || '0', style: 'tableData' },
          { text: ordem.total_material || '0', style: 'tableData' }
        ]);
      });
    });

    const chartDataTable = [
      [
        { text: 'Unidade', style: 'tableHeader' },
        { text: 'Ordens de Serviço', style: 'tableHeader' },
        { text: 'Tempo de Manutenção', style: 'tableHeader' },
        { text: 'Peças Utilizadas', style: 'tableHeader' }
      ]
    ];
  
    dados.data.data.forEach((unidade, index) => {
      chartDataTable.push([
        { text: unidade.nome, style: 'tableData' },
        { text: ordensSolicitacao[index].toString(), style: 'tableData' },
        { text: temposManutencao[index].toString(), style: 'tableData' },
        { text: pecasUtilizadas[index].toString(), style: 'tableData' }
      ]);
    });
  
    const docDefinition = {
      content: [
        { text: `Empresa: ${filtro.nomeEmpresa}`, style: 'header' },
        {
          text: `Unidade: ${filtro.nomeUnidade}\nTipo de Aplicação: ${filtro.descricaoTipoAplicacao || "Todos os Tipos de Aplicação"}\nAplicação: ${filtro.descricaoAplicacao || "Todas as aplicações"}\nManutentor: ${filtro.nomeManutentor || "Todos os Manutentores"}\nSolicitante: ${filtro.nomeSolicitante || "Todos os Solicitantes"}\n Período ${filtro.dataInicial || ""} -${filtro.dataFinal || " Período não informado"}\nGerado Em: ${dataHora}`,
          style: 'subheader',
          margin: [0, 10, 0, 20]
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', '*', '*', '*'],
            body: tableBody
          },
          layout: 'lightHorizontalLines' // Estilo de tabela mais elegante
        },
        {
          image: chartBase64,
          width: 500,
          alignment: 'center',
          margin: [0, 20, 0, 0]
        },
        {
          text: 'Dados do Gráfico',
          style: 'subheader',
          margin: [0, 20, 0, 10]
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', '*', '*', '*'],
            body: chartDataTable
          },
          layout: 'lightHorizontalLines' // Estilo de tabela mais elegante
        }
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 10]
        },
        subheader: {
          fontSize: 14,
          margin: [0, 10, 0, 10]
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
          fillColor: '#CCCCCC'
        },
        tableData: {
          margin: [0, 5, 0, 5],
          fontSize: 11
        }
      }
    };
  
    pdfMake.createPdf(docDefinition).download('RelatorioHistoricoManutencao.pdf');
  };
  

  // Prepare data for the chart
  const unidades = dados.data.data.map(unidades => unidades.nome);
  const ordensSolicitacao = dados.data.data.map(unidades => unidades.ordens_servico.length);
  const temposManutencao = dados.data.data.map(unidades => unidades.ordens_servico.reduce((sum, ordem) => sum + parseFloat(ordem.tempo_total_manutencao || 0), 0));
  const pecasUtilizadas = dados.data.data.map(unidades => unidades.ordens_servico.reduce((sum, ordem) => sum + (ordem.total_material || 0), 0));

  const chartData = {
    labels: unidades,
    datasets: [
      {
        label: 'Ordens de Serviço',
        data: ordensSolicitacao,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        maxBarThickness: 70, // Increased thickness
      },
      {
        label: 'Tempo de Manutenção',
        data: temposManutencao,
        backgroundColor: 'rgba(255, 206, 86, 0.6)',
        maxBarThickness: 70, // Increased thickness
      },
      {
        label: 'Peças Utilizadas',
        data: pecasUtilizadas,
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
        maxBarThickness: 70, // Increased thickness
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Gráfico de Histórico de Manutenção - Unidades`,
      },
    },
    scales: {
      x: {
        categoryPercentage: 0.8, // Adjusted for more spacing
        barPercentage: 0.9, // Adjusted for more spacing
      },
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <Container align="left" className="d-flex">
      <div className="border border-dark p-3">
        <div>
          <h3>Empresa: {filtro.nomeEmpresa}</h3>
        </div>
        <div>
          <ul className="list-unstyled">
            <li><strong>Unidade:</strong> {filtro.nomeUnidade}</li>
            <li><strong>Tipo de Aplicação:</strong> {filtro.descricaoAplicacao || "Todos os Tipos de Aplicação"}</li>
            <li><strong>Aplicação:</strong> {filtro.descricaoAplicacao || "Todas as Aplicações"}</li>
            <li><strong>Solicitante:</strong>{filtro.nomeSolicitante || "Todos os Solicitantes"}</li>
            <li><strong>Manutentor:</strong> {filtro.nomeManutentor || "Todos os Manutentores"}</li>
            <li><strong>Período:</strong> {filtro.dataInicial || ""} - {filtro.dataFinal || "Período não informado"}</li>
            <li><strong>Gerado Em:</strong> {dataHora}</li>
          </ul>
        </div>
        <Card>
        <button
            onClick={generatePDF}
            style={{
              position: "fixed",
              bottom: 20,
              right: 20,
              zIndex: 9999,
              backgroundColor: "#FF0000",
              padding: 20,
              borderRadius: 40,
              border: "none",
              cursor: "pointer",
            }}
          >
            <FaFilePdf size={25} color="white" /> {/* Ícone de PDF */}
          </button>
          <button
            onClick={handleForm}
            style={{
              position: "fixed",
              bottom: 20,
              right: 90,
              zIndex: 9999,
              backgroundColor: "#41B06E",
              padding: 20,
              borderRadius: 40,
              border: "none",
              cursor: "pointer",
            }}
          >
            <FaPlus size={25} color="white" />
          </button>
          <button
            onClick={handleGoHome}
            style={{
              position: "fixed",
              bottom: 20,
              right: 160,
              zIndex: 9999,
              backgroundColor: "#B4B4B8",
              padding: 20,
              borderRadius: 40,
              border: "none",
              cursor: "pointer",
            }}
          >
            <FaHome size={25} color="white" />
          </button>
          <div className="table-responsive">
            <Table striped hover>
              <thead className="thead-light">
                <tr>
                  <th>Unidade</th>
                  <th>Ordens de Serviço</th>
                  <th>Tempo de Manutenção</th>
                  <th>Peças Utilizadas</th>
                </tr>
              </thead>
              <tbody>
                {dados.data.data.map((unidades, idx) =>
                  unidades.ordens_servico.map((ordem, ordemIdx) => (
                    <tr key={`${idx}-${ordemIdx}`}>
                      <td>{unidades.nome || 'Unidade não Informada'}</td>
                      <td>{`${ordem.id || 'N/A'} - ${ordem.solicitacao || 'N/A'}`}</td>
                      <td>{ordem.tempo_total_manutencao || '0'}</td>
                      <td>{ordem.total_material || '0'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card>
        <div style={{ width: '600px', height: '400px', marginTop: '20px', textAlign: 'left'}}>
          <Bar ref={chartRef} data={chartData} options={chartOptions} />
        </div>
      </div>
    </Container>
  );
};

export default RelatorioHistoricoManutencao;
